import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { getImageUrl, getSubscriptionPlans, upgradeSubscription } from '../services/api';
import SubscriptionPaymentModal from '../components/SubscriptionPaymentModal';
import { ownsEvents } from '../utils/roles';
import './Dashboard.css';

const roleLabels = {
  photographer: 'Photographe',
  user: 'Utilisateur',
};

const Dashboard = () => {
  const { user, setUser, isAuthenticated, loading: authLoading } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchDashboardData();
    }
  }, [isAuthenticated, user]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/analytics/dashboard');
      setDashboardData(response);
    } catch (err) {
      console.error('Dashboard data error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Chargement de votre tableau de bord...</p>
      </div>
    );
  }

  // User must be authenticated (ProtectedRoute should handle this)
  if (!isAuthenticated || !user) {
    return null;
  }

  return (
    <div className="dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="welcome-section">
          <h1>Bienvenue, {user.name} !</h1>
          <p className="subtitle">
            {ownsEvents(user)
              ? 'Gérez vos événements et importez des photos pour vos clients'
              : 'Découvrez les photos où vous apparaissez grâce à la reconnaissance faciale par IA'}
          </p>
        </div>

        <div className="user-profile-badge">
          <div className="profile-avatar">
            {user.profile_photo ? (
              <img src={ getImageUrl(user.profile_photo) } alt={user.name} />
            ) : (
              <div className="avatar-placeholder">
                {user.name.charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <div className="profile-info">
            <span className="profile-name">{user.name}</span>
            <span className="profile-role">{roleLabels[user.role] || user.role}</span>
          </div>
        </div>
      </div>

      {/* Quick Stats Section */}
      {!loading && dashboardData && (
        <div className="dashboard-stats">
          {ownsEvents(user) ? (
            <PhotographerStats data={dashboardData} />
          ) : (
            <UserStats data={dashboardData} />
          )}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="stats-loading">
          <div className="spinner-small"></div>
          <p>Chargement des statistiques...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-banner">
          <span className="error-icon">⚠️</span>
          <p>Impossible de charger les données du tableau de bord : {error}</p>
          <button onClick={fetchDashboardData} className="btn-retry">
            Réessayer
          </button>
        </div>
      )}

      {ownsEvents(user) && (
        <PhotographerSubscription user={user} setUser={setUser} />
      )}

      {/* Quick Actions Section */}
      <div className="dashboard-actions">
        <h2>Actions rapides</h2>
        <div className="actions-grid">
          {ownsEvents(user) ? (
            <PhotographerActions navigate={navigate} />
          ) : (
            <UserActions navigate={navigate} user={user} />
          )}
        </div>
      </div>

      {/* Recent Activity Section */}
      <div className="dashboard-recent">
        <h2>Activité récente</h2>
        {ownsEvents(user) ? (
          <PhotographerActivity />
        ) : (
          <UserActivity />
        )}
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER STATS COMPONENT
// ============================================================================
const PhotographerStats = ({ data }) => {
  return (
    <>
      <StatCard
        icon="📅"
        label="Événements"
        value={data.events || 0}
        color="#4F46E5"
      />
      <StatCard
        icon="📸"
        label="Photos"
        value={(data.photos || 0).toLocaleString()}
        color="#4ADE80"
      />
      <StatCard
        icon="💾"
        label="Stockage serveur"
        value={formatStorage(data.storage_used_bytes, data.storage_used_mb)}
        color="#60A5FA"
      />
      <StatCard
        icon="💰"
        label="Revenus"
        value={`${Number(data.revenue || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F`}
        color="#60A5FA"
      />
    </>
  );
};

const formatStorage = (bytes, megabytes) => {
  const sizeInBytes = Number.isFinite(Number(bytes))
    ? Number(bytes)
    : Number(megabytes || 0) * 1048576;

  if (sizeInBytes <= 0) return '0 MB';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let value = sizeInBytes;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const decimals = value >= 10 || unitIndex < 2 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[unitIndex]}`;
};

// ============================================================================
// PHOTOGRAPHER SUBSCRIPTION
// ============================================================================
const PhotographerSubscription = ({ user, setUser }) => {
  const [plans, setPlans] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [message, setMessage] = useState(null);
  const [paymentPlanKey, setPaymentPlanKey] = useState(null);
  const currentPlan = user.subscription_plan || 'free';

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await getSubscriptionPlans();
      setPlans(response.plans || {});
    } catch (error) {
      console.error('Subscription plans error:', error);
      setMessage({
        type: 'error',
        text: error.message || "Impossible de charger les forfaits d'abonnement."
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelection = async (planKey) => {
    if (planKey === currentPlan || selectedPlan) return;

    const planPrice = Number(plans?.[planKey]?.price_monthly ?? plans?.[planKey]?.price ?? 0);
    if (planPrice > 0) {
      setMessage(null);
      setPaymentPlanKey(planKey);
      return;
    }

    try {
      setSelectedPlan(planKey);
      setMessage(null);
      const response = await upgradeSubscription({ plan: planKey });
      const updatedUser = { ...user, subscription_plan: planKey };

      setUser(updatedUser);
      localStorage.setItem('userData', JSON.stringify(updatedUser));
      setMessage({
        type: 'success',
        text: response.message || response.data?.message || 'Votre forfait a été mis à jour.'
      });
    } catch (error) {
      console.error('Subscription upgrade error:', error);
      setMessage({
        type: 'error',
        text: error.message || error.response?.data?.message || 'Impossible de mettre à jour votre forfait.'
      });
    } finally {
      setSelectedPlan(null);
    }
  };

  const handleSubscriptionPaymentSuccess = (planKey) => {
    setPaymentPlanKey(null);
    const updatedUser = { ...user, subscription_plan: planKey };
    setUser(updatedUser);
    localStorage.setItem('userData', JSON.stringify(updatedUser));
    setMessage({ type: 'success', text: 'Votre abonnement a été activé.' });
  };

  const getPlanColor = (planKey) => {
    switch (planKey) {
      case 'basic': return '#22C55E';
      case 'pro': return '#6366F1';
      case 'enterprise': return '#A855F7';
      default: return '#71717A';
    }
  };

  const getPlanIcon = (planKey) => {
    switch (planKey) {
      case 'basic': return '✓';
      case 'pro': return '★';
      case 'enterprise': return '♛';
      default: return '↗';
    }
  };

  const formatPlanName = (planKey, plan) => {
    if (plan?.name) return plan.name;
    return planKey.charAt(0).toUpperCase() + planKey.slice(1);
  };

  if (loading) {
    return (
      <section className="subscription-section">
        <div className="subscription-header">
          <div>
            <h2>Forfaits photographe</h2>
            <p>Chargement des forfaits disponibles...</p>
          </div>
          <div className="spinner-small"></div>
        </div>
      </section>
    );
  }

  if (!plans || !Object.keys(plans).length) {
    return (
      <section className="subscription-section">
        <div className="subscription-header">
          <div>
            <h2>Forfaits photographe</h2>
            <p>Aucun forfait d'abonnement n'est disponible pour le moment.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="subscription-section">
      <div className="subscription-header">
        <div>
          <h2>Choisissez votre forfait</h2>
          <p>Sélectionnez l'abonnement adapté à votre volume d'événements et de photos.</p>
        </div>
        <div className="current-plan-pill">
          Actuel : {formatPlanName(currentPlan, plans[currentPlan])}
        </div>
      </div>

      {message && (
        <div className={`subscription-message ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="subscription-grid">
        {Object.entries(plans).map(([planKey, plan]) => {
          const planColor = getPlanColor(planKey);
          const isCurrentPlan = currentPlan === planKey;
          const isProcessing = selectedPlan === planKey;

          return (
            <div
              key={planKey}
              className={`subscription-card ${isCurrentPlan ? 'current' : ''} ${planKey === 'pro' ? 'featured' : ''}`}
              style={{ borderColor: isCurrentPlan ? planColor : undefined }}
            >
              {planKey === 'pro' && <span className="popular-badge">Populaire</span>}
              {isCurrentPlan && <span className="current-badge" style={{ background: planColor }}>Actuel</span>}

              <div className="plan-heading">
                <span className="plan-icon" style={{ color: planColor }}>{getPlanIcon(planKey)}</span>
                <h3>{formatPlanName(planKey, plan)}</h3>
              </div>

              <div className="plan-price">
                {Number(plan.price || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} F
                <span>/mois</span>
              </div>

              <ul className="plan-features">
                {(plan.features || []).map((feature, index) => (
                  <li key={index}>
                    <span style={{ color: planColor }}>✓</span>
                    {feature}
                  </li>
                ))}
              </ul>

              <button
                className="plan-select-button"
                onClick={() => handlePlanSelection(planKey)}
                disabled={isCurrentPlan || selectedPlan}
                style={{ background: isCurrentPlan ? '#3F3F46' : planColor }}
              >
                {isCurrentPlan ? 'Forfait actuel' : isProcessing ? 'Mise à jour...' : 'Choisir ce forfait'}
              </button>
            </div>
          );
        })}
      </div>

      <SubscriptionPaymentModal
        isOpen={!!paymentPlanKey}
        onClose={() => setPaymentPlanKey(null)}
        plan={paymentPlanKey ? plans?.[paymentPlanKey] : null}
        planKey={paymentPlanKey}
        billingCycle="monthly"
        onSuccess={handleSubscriptionPaymentSuccess}
      />
    </section>
  );
};

// ============================================================================
// USER STATS COMPONENT
// ============================================================================
const UserStats = ({ data }) => {
  return (
    <>
      <StatCard
        icon="📸"
        label="Photos trouvées"
        value={data.matches || 0}
        color="#4F46E5"
      />
      <StatCard
        icon="📅"
        label="Événements"
        value={data.events || 0}
        color="#4ADE80"
      />
      <StatCard
        icon="⬇️"
        label="Téléchargements"
        value={data.downloads || 0}
        color="#60A5FA"
      />
    </>
  );
};

// ============================================================================
// STAT CARD COMPONENT
// ============================================================================
const StatCard = ({ icon, label, value, color }) => {
  return (
    <div className="stat-card" style={{ borderLeftColor: color }}>
      <div className="stat-icon" style={{ color }}>
        {icon}
      </div>
      <div className="stat-content">
        <span className="stat-label">{label}</span>
        <span className="stat-value" style={{ color }}>
          {value}
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// PHOTOGRAPHER QUICK ACTIONS
// ============================================================================
const PhotographerActions = ({ navigate }) => {
  return (
    <>
      <ActionCard
        icon="➕"
        title="Créer un événement"
        description="Lancer un nouvel événement photo"
        onClick={() => navigate('/create-event')}
        color="#4ADE80"
      />
      {/*}
      <ActionCard
        icon="📤"
        title="Importer des photos"
        description="Ajouter des photos à vos événements"
        onClick={() => navigate('/photos/upload')}
        color="#60A5FA"
      />
      */}
      <ActionCard
        icon="📊"
        title="Voir les analyses"
        description="Consulter vos indicateurs de performance"
        onClick={() => navigate('/analytics')}
        color="#4F46E5"
      />
      <ActionCard
        icon="⚙️"
        title="Paramètres"
        description="Gérer votre compte"
        onClick={() => navigate('/settings')}
        color="#C084FC"
      />
    </>
  );
};

// ============================================================================
// USER QUICK ACTIONS
// ============================================================================
const UserActions = ({ navigate, user }) => {
  return (
    <>
      <ActionCard
        icon="🔍"
        title="Trouver mes photos"
        description="Rechercher les photos où vous apparaissez"
        onClick={() => navigate('/my-photos')}
        color="#4F46E5"
      />
      <ActionCard
        icon="📊"
        title="Voir les analyses"
        description="Consulter vos statistiques photo"
        onClick={() => navigate('/analytics')}
        color="#4ADE80"
      />
      {!user.face_encoded && (
        <ActionCard
          icon="📷"
          title="Importer une photo de profil"
          description="Activer la reconnaissance faciale"
          onClick={() => navigate('/profile')}
          color="#F87171"
          highlight
        />
      )}
      <ActionCard
        icon="⚙️"
        title="Paramètres"
        description="Gérer votre compte"
        onClick={() => navigate('/settings')}
        color="#C084FC"
      />
    </>
  );
};

// ============================================================================
// ACTION CARD COMPONENT
// ============================================================================
const ActionCard = ({ icon, title, description, onClick, color, highlight }) => {
  return (
    <div
      className={`action-card ${highlight ? 'highlight' : ''}`}
      onClick={onClick}
      style={{ borderColor: color }}
    >
      <div className="action-icon" style={{ color }}>
        {icon}
      </div>
      <div className="action-content">
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
      <div className="action-arrow" style={{ color }}>
        →
      </div>
    </div>
  );
};

// ============================================================================
// ACTIVITY HELPERS
// ============================================================================

/** "il y a 3 jours" — absolute dates read poorly in a recent-activity feed. */
const relativeDate = (value) => {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  const diffDays = Math.round((Date.now() - date.getTime()) / 86400000);

  if (diffDays < 0) {
    const inDays = Math.abs(diffDays);
    if (inDays === 0) return "aujourd'hui";
    if (inDays === 1) return 'demain';
    if (inDays < 31) return `dans ${inDays} jours`;
    return date.toLocaleDateString('fr-FR');
  }

  if (diffDays === 0) return "aujourd'hui";
  if (diffDays === 1) return 'hier';
  if (diffDays < 31) return `il y a ${diffDays} jours`;
  if (diffDays < 365) return `il y a ${Math.round(diffDays / 30)} mois`;

  return date.toLocaleDateString('fr-FR');
};

const ActivityState = ({ children, onRetry }) => (
  <div className="activity-state">
    <p>{children}</p>
    {onRetry && (
      <button type="button" className="activity-retry" onClick={onRetry}>
        Réessayer
      </button>
    )}
  </div>
);

const ActivitySkeleton = ({ rows = 3 }) => (
  <div className="activity-list" aria-hidden="true">
    {Array.from({ length: rows }).map((_, index) => (
      <div className="activity-item is-skeleton" key={index}>
        <div className="activity-thumb skeleton-block" />
        <div className="activity-content">
          <span className="skeleton-line skeleton-line-title" />
          <span className="skeleton-line skeleton-line-text" />
        </div>
      </div>
    ))}
  </div>
);

// ============================================================================
// PHOTOGRAPHER RECENT ACTIVITY
// ============================================================================
const PhotographerActivity = () => {
  const navigate = useNavigate();
  const [recentEvents, setRecentEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetchRecentEvents();
  }, []);

  const fetchRecentEvents = async () => {
    try {
      setLoading(true);
      setFailed(false);
      const response = await api.get('/events?limit=5');
      setRecentEvents((response.events || []).slice(0, 5));
    } catch (error) {
      console.error('Recent events error:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <ActivitySkeleton />;

  if (failed) {
    return (
      <ActivityState onRetry={fetchRecentEvents}>
        Impossible de charger vos événements récents.
      </ActivityState>
    );
  }

  if (!recentEvents.length) {
    return (
      <div className="empty-state">
        <p>Aucun événement récent. Créez votre premier événement !</p>
        <button type="button" className="activity-cta" onClick={() => navigate('/create-event')}>
          Créer un événement
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="activity-list">
        {recentEvents.map((event) => {
          const when = relativeDate(event.event_date);
          const photoCount = event.photos_count || 0;

          return (
            <button
              type="button"
              key={event.id}
              className="activity-item"
              onClick={() => navigate(`/event/${event.id}/photos`)}
            >
              <div className="activity-thumb activity-thumb-icon">📅</div>
              <div className="activity-content">
                <h4>{event.name}</h4>
                <p>
                  {[event.category, when].filter(Boolean).join(' • ')}
                </p>
              </div>
              <div className="activity-stats">
                <strong>{photoCount}</strong>
                <span>photo{photoCount === 1 ? '' : 's'}</span>
              </div>
            </button>
          );
        })}
      </div>
      <button type="button" className="activity-more" onClick={() => navigate('/photographer')}>
        Voir tous mes événements →
      </button>
    </>
  );
};

// ============================================================================
// USER RECENT ACTIVITY
// ============================================================================
const UserActivity = () => {
  const navigate = useNavigate();
  const [recentMatches, setRecentMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetchRecentMatches();
  }, []);

  const fetchRecentMatches = async () => {
    try {
      setLoading(true);
      setFailed(false);
      const response = await api.get('/my-photos?limit=5');
      const allPhotos = (response.data || []).flatMap((event) =>
        (event.photos || []).map((photo) => ({
          ...photo,
          event_name: event.event_name,
          event_category: event.event_category,
        }))
      ).slice(0, 5);
      setRecentMatches(allPhotos);
    } catch (error) {
      console.error('Recent matches error:', error);
      setFailed(true);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <ActivitySkeleton />;

  if (failed) {
    return (
      <ActivityState onRetry={fetchRecentMatches}>
        Impossible de charger vos photos récentes.
      </ActivityState>
    );
  }

  if (!recentMatches.length) {
    return (
      <div className="empty-state">
        <p>Aucune photo trouvée pour le moment. Importez une photo de profil pour commencer !</p>
        <button type="button" className="activity-cta" onClick={() => navigate('/profile')}>
          Importer une photo de profil
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="activity-list">
        {recentMatches.map((match, index) => {
          const score = Number(match.confidence_score);
          const hasScore = Number.isFinite(score);
          const when = relativeDate(match.uploaded_at);

          return (
            <button
              type="button"
              key={match.id || index}
              className="activity-item"
              onClick={() => navigate('/my-photos')}
            >
              <div className="activity-thumb">
                {match.filename ? (
                  <img
                    src={getImageUrl(match.filename)}
                    alt=""
                    loading="lazy"
                    onError={(e) => { e.currentTarget.style.display = 'none'; }}
                  />
                ) : (
                  <span className="activity-thumb-icon">📸</span>
                )}
              </div>
              <div className="activity-content">
                <h4>{match.event_name || 'Événement'}</h4>
                <p>{[match.event_category, when].filter(Boolean).join(' • ')}</p>
              </div>
              {hasScore && (
                <div
                  className={`activity-badge ${score >= 90 ? 'is-strong' : ''}`}
                  title={`${score.toFixed(1)}% de correspondance`}
                >
                  {score.toFixed(0)}%
                </div>
              )}
            </button>
          );
        })}
      </div>
      <button type="button" className="activity-more" onClick={() => navigate('/my-photos')}>
        Voir toutes mes photos →
      </button>
    </>
  );
};

export default Dashboard;
